import {
    ApiFullLandingPageLink,
    ApiImageDimensions,
    ApiShopperLandingPageLink,
    ApiPdpSellingPlanGroup,
    isApiFullLandingPageLink,
    isGroupedProductLink,
    MATCH_THRESHOLD,
    ProductDisplayData,
    VLPOption,
    VLPVariant,
    buildOptionKeyMap,
    canonicalizePerceptualHash,
    hasUsablePerceptualHash,
    matchesAnyHashAtThreshold,
    selectClosestAvailableVariant,
    selectVariantWithFallback,
} from '@storefronts/common-types'

export type VirtualProductDisplayData = Pick<
    ProductDisplayData,
    | 'title'
    | 'reviews'
    | 'carouselImages'
    | 'carouselImagePerceptualHashes'
    | 'bodyContent'
    | 'bodyBadges'
    | 'reviewAnalysis'
    | 'productOptionGuide'
> &
    Pick<ApiFullLandingPageLink, 'reviewContent'>

/**
 * Underlying Shopify product id for {@link virtualProductDisplayData} lookups when no variant
 * is selected (matches PDP default variant). Null for non-virtual links or missing variant data.
 */
export function getDefaultVirtualProductDisplayShopifyProductId(
    lpLink: ApiShopperLandingPageLink,
): string | null {
    if (!isGroupedProductLink(lpLink)) return null
    const variantInfo = lpLink.variantInfo ?? []
    const defaultVariantId = lpLink.renderedDefaultVariantId
    if (defaultVariantId) {
        const v = variantInfo.find((x) => x.shopifyId === defaultVariantId)
        if (v?.shopifyProductId) return v.shopifyProductId
    }
    const first = variantInfo.find((x) => x.shopifyProductId)
    return first?.shopifyProductId ?? null
}

function resolvedUnderlyingShopifyProductId(
    lpLink: ApiShopperLandingPageLink,
    shopifyProductId?: string | null,
): string | null {
    const explicit =
        shopifyProductId != null && shopifyProductId !== ''
            ? shopifyProductId
            : null
    return explicit ?? getDefaultVirtualProductDisplayShopifyProductId(lpLink)
}

/**
 * Subscription selling plan groups for the current selection. Virtual-product
 * links carry plans per underlying Shopify product, so each variant resolves
 * its own product's plans; non-virtual links fall back to the link-level set.
 * Groups attached to specific variants are only offered for those variants.
 */
export function getSellingPlanGroups(
    lpLink: ApiShopperLandingPageLink,
    shopifyProductId?: string | null,
    variantId?: string | null,
): ApiPdpSellingPlanGroup[] | undefined {
    const groups = getProductSellingPlanGroups(lpLink, shopifyProductId)
    if (!variantId) {
        return groups
    }
    return groups?.filter(
        (group) => !group.variantIds || group.variantIds.includes(variantId),
    )
}

function getProductSellingPlanGroups(
    lpLink: ApiShopperLandingPageLink,
    shopifyProductId?: string | null,
): ApiPdpSellingPlanGroup[] | undefined {
    const byProductId = lpLink.sellingPlanGroupsByProductId
    if (byProductId) {
        const underlyingId = resolvedUnderlyingShopifyProductId(
            lpLink,
            shopifyProductId,
        )
        return underlyingId ? byProductId[underlyingId] : undefined
    }
    return lpLink.sellingPlanGroups
}

/**
 * Whether the current selection can only be purchased via a subscription.
 * Virtual-product links resolve per underlying Shopify product (missing map
 * entries are one-time–eligible); non-virtual links use the link-level flag.
 */
export function getRequiresSellingPlan(
    lpLink: ApiShopperLandingPageLink,
    shopifyProductId?: string | null,
): boolean {
    if (lpLink.sellingPlanGroupsByProductId) {
        const underlyingId = resolvedUnderlyingShopifyProductId(
            lpLink,
            shopifyProductId,
        )
        return underlyingId
            ? !!lpLink.requiresSellingPlanByProductId?.[underlyingId]
            : false
    }
    return lpLink.requiresSellingPlan ?? false
}

/**
 * Shopify / subscription-app option values that mean a one-time purchase
 * (e.g. Order Type: "One-Time"). Used to hide those choices when the product
 * requires a selling plan.
 */
export function isOneTimePurchaseOptionValue(value: string): boolean {
    const normalized = value
        .trim()
        .toLowerCase()
        .replace(/[-_]+/g, ' ')
        .replace(/\s+/g, ' ')
    return (
        normalized === 'one time' ||
        normalized === 'onetime' ||
        normalized === 'one time purchase' ||
        normalized === 'otp'
    )
}

export function variantHasOneTimePurchaseOption(variant: VLPVariant): boolean {
    return [variant.option1, variant.option2, variant.option3].some(
        (value) => value != null && isOneTimePurchaseOptionValue(value),
    )
}

/**
 * Drops one-time purchase option values from PDP option rows, except values
 * still reachable via `remainingVariants` (e.g. a virtual-product link whose
 * other underlying product allows one-time purchase).
 */
export function filterOneTimePurchaseOptionValues(
    optionInfo: VLPOption[] | undefined,
    remainingVariants: VLPVariant[],
): VLPOption[] {
    const options = optionInfo ?? []
    const optionKeyMap = buildOptionKeyMap(options)
    return options
        .map((option) => {
            const key = optionKeyMap[option.name]
            return {
                ...option,
                values: option.values.filter(({ value }) => {
                    if (!isOneTimePurchaseOptionValue(value)) return true
                    return (
                        !!key && remainingVariants.some((v) => v[key] === value)
                    )
                }),
            }
        })
        .filter((option) => option.values.length > 0)
}

const VARIANT_OPTION_SLOTS = ['option1', 'option2', 'option3'] as const

/**
 * Whether `variant` agrees with `targetVariant` on every option slot that
 * isn't the one-time/subscription order-type slot (e.g. size, color).
 */
function matchesNonOrderTypeOptions(
    variant: VLPVariant,
    targetVariant: VLPVariant,
): boolean {
    return VARIANT_OPTION_SLOTS.every((slot) => {
        const targetValue = targetVariant[slot]
        if (targetValue == null || isOneTimePurchaseOptionValue(targetValue)) {
            return true
        }
        return variant[slot] === targetValue
    })
}

/**
 * Prefer a non-one-time variant for subscription-only products whose option
 * set still includes a "One-Time" value. When `targetVariant` is given (the
 * shopper's current selection or deep-linked variant), prefers a subscribable
 * variant that keeps its other options (size, color, etc.) so a shopper isn't
 * bounced to an unrelated option combination. On virtual-product links,
 * candidates are scoped to `targetVariant`'s underlying product so a
 * subscription-only variant is never swapped for a different product's
 * variant.
 */
export function pickSubscribableVariant(
    variants: VLPVariant[] | undefined,
    targetVariant?: VLPVariant | null,
): VLPVariant | null {
    if (!variants?.length) return null

    const candidates = targetVariant?.shopifyProductId
        ? variants.filter(
              (variant) =>
                  variant.shopifyProductId === targetVariant.shopifyProductId,
          )
        : variants

    const subscribable = candidates.filter(
        (variant) => !variantHasOneTimePurchaseOption(variant),
    )
    if (!subscribable.length) return null

    if (targetVariant) {
        const matching = subscribable.filter((variant) =>
            matchesNonOrderTypeOptions(variant, targetVariant),
        )
        const availableMatch = matching.find(
            (variant) => variant.available !== false,
        )
        if (availableMatch) return availableMatch
        if (matching.length) return matching[0]
    }

    return (
        subscribable.find((variant) => variant.available !== false) ??
        subscribable[0]
    )
}

export type ResolveDefaultPdpVariantOptions = {
    initialVariantShopifyId?: string | null
    preferAvailable?: boolean
    preferredOptionName?: string | null
}

/**
 * Default PDP variant for a link, preferring a non-one-time option when the
 * product requires a selling plan.
 */
export function resolveDefaultPdpVariant(
    lpLink: ApiShopperLandingPageLink,
    options?: ResolveDefaultPdpVariantOptions | null,
): VLPVariant | null {
    if (!lpLink.variantInfo?.length) return null

    let chosen = selectVariantWithFallback({
        variants: lpLink.variantInfo,
        selectedVariantId: options?.initialVariantShopifyId || undefined,
        defaultVariantId: lpLink.renderedDefaultVariantId,
    })
    if (options?.preferAvailable) {
        chosen = selectClosestAvailableVariant({
            variants: lpLink.variantInfo,
            options: lpLink.optionInfo ?? [],
            targetVariant: chosen,
            preferredOptionName: options.preferredOptionName,
        })
    }
    if (
        chosen &&
        getRequiresSellingPlan(lpLink, chosen.shopifyProductId) &&
        variantHasOneTimePurchaseOption(chosen)
    ) {
        return pickSubscribableVariant(lpLink.variantInfo, chosen) ?? chosen
    }
    return chosen
}

/**
 * When a subscription-only product is stuck on a one-time order-type variant,
 * returns the variant to switch to; otherwise null.
 */
export function getSubscriptionOnlyVariantCorrection(
    lpLink: ApiShopperLandingPageLink,
    activeVariant: VLPVariant | null,
): VLPVariant | null {
    if (
        !activeVariant ||
        !getRequiresSellingPlan(lpLink, activeVariant.shopifyProductId) ||
        !variantHasOneTimePurchaseOption(activeVariant)
    ) {
        return null
    }
    const nextVariant = pickSubscribableVariant(
        lpLink.variantInfo,
        activeVariant,
    )
    return nextVariant && nextVariant.shopifyId !== activeVariant.shopifyId
        ? nextVariant
        : null
}

/**
 * Returns display data (title, reviews, reviewContent, carouselImages, bodyContent, bodyBadges,
 * productOptionGuide) for the current context. For virtual products, resolves underlying link data
 * using shopifyProductId when provided; otherwise uses the default variant's underlying product
 * (same as initial PDP). Falls back to the link's own fields when not applicable.
 */
export function getDisplayData(
    lpLink: ApiShopperLandingPageLink,
    shopifyProductId?: string | null,
): VirtualProductDisplayData {
    const underlyingId = resolvedUnderlyingShopifyProductId(
        lpLink,
        shopifyProductId,
    )
    if (isApiFullLandingPageLink(lpLink)) {
        const variantData =
            underlyingId != null
                ? lpLink.virtualProductDisplayData?.[underlyingId]
                : undefined
        if (variantData) {
            return {
                title: variantData.title ?? lpLink.title,
                reviews: variantData.reviews ?? lpLink.reviews,
                reviewContent: lpLink.reviewContent,
                carouselImages:
                    variantData.carouselImages ?? lpLink.carouselImages,
                carouselImagePerceptualHashes:
                    variantData.carouselImagePerceptualHashes,
                bodyContent:
                    variantData.bodyContent !== undefined
                        ? variantData.bodyContent
                        : lpLink.bodyContent,
                bodyBadges: variantData.bodyBadges,
                reviewAnalysis: variantData.reviewAnalysis,
                productOptionGuide:
                    variantData.productOptionGuide ?? lpLink.productOptionGuide,
            }
        }
        return {
            title: lpLink.title,
            reviews: lpLink.reviews,
            reviewContent: lpLink.reviewContent,
            carouselImages: lpLink.carouselImages,
            carouselImagePerceptualHashes: lpLink.carouselImagePerceptualHashes,
            bodyContent: lpLink.bodyContent,
            bodyBadges: lpLink.bodyBadges,
            reviewAnalysis: lpLink.reviewAnalysis,
            productOptionGuide: lpLink.productOptionGuide,
        }
    }

    const variantData =
        underlyingId != null
            ? lpLink.virtualProductDisplayData?.[underlyingId]
            : undefined
    if (variantData) {
        return {
            title: variantData.title ?? lpLink.title,
            reviews: variantData.reviews ?? lpLink.reviews,
            reviewContent: undefined,
            carouselImages: variantData.carouselImages,
            carouselImagePerceptualHashes: undefined,
            bodyContent: undefined,
            bodyBadges: variantData.bodyBadges,
            reviewAnalysis: undefined,
            productOptionGuide: undefined,
        }
    }
    return {
        title: lpLink.title,
        reviews: lpLink.reviews,
        reviewContent: undefined,
        carouselImages: undefined,
        carouselImagePerceptualHashes: undefined,
        bodyContent: undefined,
        bodyBadges: lpLink.bodyBadges,
        reviewAnalysis: undefined,
        productOptionGuide: undefined,
    }
}

/**
 * Resolves the primary image URL for a product card / PLP item.
 * Priority: active variant's featured image > virtual product carousel > link-level image.
 * Delegates to {@link resolveProductCardImageUrls} so a card's thumbnail always
 * matches the first slide of its carousel.
 */
export function resolveProductCardImageUrl(
    activeVariant: VLPVariant | null,
    lpLink: Pick<ApiShopperLandingPageLink, 'virtualProductId' | 'imageUrl'>,
    displayData: Pick<VirtualProductDisplayData, 'carouselImages'>,
): string {
    return (
        resolveProductCardImageUrls(activeVariant, lpLink, displayData)[0] ??
        lpLink.imageUrl
    )
}

export function resolveProductCardImageDimensions(
    imageUrl: string,
    activeVariant: VLPVariant | null,
    lpLink: Pick<ApiShopperLandingPageLink, 'imageDimensions'>,
): ApiImageDimensions | undefined {
    if (
        activeVariant?.featuredImage === imageUrl &&
        activeVariant.featuredImageDimensions
    ) {
        return activeVariant.featuredImageDimensions
    }
    return lpLink.imageDimensions?.[imageUrl]
}

export function resolveProductCardImageUrls(
    activeVariant: VLPVariant | null,
    lpLink: Pick<ApiShopperLandingPageLink, 'virtualProductId' | 'imageUrl'>,
    displayData: Pick<
        VirtualProductDisplayData,
        'carouselImages' | 'carouselImagePerceptualHashes'
    >,
): string[] {
    const seen = new Set<string>()
    const urls: string[] = []
    const acceptedHashes: string[] = []
    const perceptualHashes = displayData.carouselImagePerceptualHashes
    const push = (url: string | null | undefined) => {
        if (!url || seen.has(url)) return

        const rawHash = perceptualHashes?.[url]
        if (hasUsablePerceptualHash(rawHash)) {
            const hash = canonicalizePerceptualHash(rawHash)
            if (
                matchesAnyHashAtThreshold(hash, acceptedHashes, MATCH_THRESHOLD)
            ) {
                return
            }
            acceptedHashes.push(hash)
        }

        seen.add(url)
        urls.push(url)
    }
    // Carousels exclude variant images (the selected variant's image leads via
    // this prepend). Without a selected variant, non-virtual links lead with
    // the link hero — it may itself be a variant image absent from the
    // gallery; exact-URL dedupe collapses it when it's already the first
    // carousel image. Virtual product links skip the hero (it belongs to the
    // VP, not the selected underlying product) and lead with the gallery.
    push(
        activeVariant?.featuredImage ||
            (isGroupedProductLink(lpLink) ? null : lpLink.imageUrl),
    )
    displayData.carouselImages?.forEach(push)
    if (urls.length === 0) push(lpLink.imageUrl)
    return urls.slice(0, 25)
}
